import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { ArrowLeft, Clock, Printer, User } from "lucide-react";
import { disciplineColor } from "../lib/planningHelpers";
import { api } from "./api";
import { usePagePaysage } from "./ui";

/* Une salle écrite « Salle A·B » compte pour les deux affiches. */
const lettres = (salle) =>
  String(salle || "").replace(/salle/gi, "").split(/[^A-Za-z0-9]+/).filter(Boolean);

const actif = (c) => String(c.statut || "").toLowerCase().startsWith("actif");

const duree = (min) => {
  const n = Number(min) || 0;
  if (!n) return "";
  if (n % 60 === 0) return `${n / 60}h`;
  return n > 60 ? `${Math.floor(n / 60)}h${n % 60}` : `${n} min`;
};

// Une page A4 paysage en pixels CSS, marges déduites (voir usePagePaysage dans ui.jsx).
const PAGE_LARGEUR = 1040;
const PAGE_HAUTEUR = 700;
// En dessous, l'affiche ne se lit plus à un mètre d'une porte : on préfère une deuxième
// (ou troisième) page à un texte minuscule.
const TAILLE_MIN = 11;
const TAILLE_MAX = 19;
// Mode « tenir sur une page » : l'affiche d'une seule salle doit pouvoir sortir sur une
// feuille unique, quitte à descendre plus bas que la taille de confort.
const TAILLE_UNE_PAGE = 6;

/* L'affiche de la semaine, comme la vue Semaine du site : une colonne par jour, une carte
   par cours, la couleur de sa discipline. Le texte est mesuré puis agrandi au maximum pour
   remplir exactement une page — c'est une affiche plastifiée sur une porte, pas un rapport.
   Tout ce qui sert à la manipuler porte « sans-impression ». */
export default function PlanningAffiche({ data, onFermer }) {
  const [salle, setSalle] = useState("");
  const [entete, setEntete] = useState(null);
  const [base, setBase] = useState(11);
  const [ajuste, setAjuste] = useState(0);
  const [pages, setPages] = useState(1);
  // Une salle seule tient presque toujours sur une feuille : c'est ce que l'école
  // affiche sur la porte. « Toutes les salles » reste en texte lisible, sur 2-3 pages.
  const [unePage, setUnePage] = useState(false);
  const feuille = useRef(null);
  const taille = Math.max(9, Math.min(26, base + ajuste));
  usePagePaysage();

  useEffect(() => { api("/reglages").then(setEntete).catch(() => {}); }, []);

  const sallesDispo = useMemo(() => {
    const vues = new Set();
    (data.jours || []).forEach((j) => j.cours.forEach((c) => {
      if (actif(c)) lettres(c.salle).forEach((l) => vues.add(l));
    }));
    return [...vues].sort();
  }, [data]);

  const jours = useMemo(() => (data.jours || [])
    .map((j) => ({
      jour: j.jour,
      cours: j.cours
        .filter((c) => actif(c) && (!salle || lettres(c.salle).includes(salle)))
        .sort((a, b) => String(a.debut).localeCompare(String(b.debut))),
    }))
    .filter((j) => j.jour !== "Dimanche" || j.cours.length > 0), [data, salle]);

  const total = jours.reduce((n, j) => n + j.cours.length, 0);

  // La plus grande taille de texte qui tient, sans jamais descendre sous ce qui se lit à
  // un mètre : on ajoute des pages plutôt que de rapetisser le texte.
  useLayoutEffect(() => {
    const el = feuille.current;
    if (!el) return;
    const largeurAvant = el.style.width;
    el.style.width = `${PAGE_LARGEUR}px`;
    const tient = (taille, hauteur) => {
      el.style.fontSize = `${taille}px`;
      return el.scrollHeight <= hauteur;
    };
    let choisi = TAILLE_MIN;
    let nb = 1;
    if (unePage) {
      let trouve = 0;
      for (let f = TAILLE_MAX; f >= TAILLE_UNE_PAGE; f -= 0.5) {
        if (tient(f, PAGE_HAUTEUR)) { trouve = f; break; }
      }
      el.style.width = largeurAvant;
      el.style.fontSize = "";
      setBase(trouve || TAILLE_UNE_PAGE);
      setAjuste(0);
      setPages(1);
      return;
    }
    for (const feuilles of [1, 2, 3]) {
      let trouve = 0;
      for (let f = TAILLE_MAX; f >= TAILLE_MIN; f -= 0.5) {
        if (tient(f, PAGE_HAUTEUR * feuilles)) { trouve = f; break; }
      }
      if (trouve) { choisi = trouve; nb = feuilles; break; }
      nb = feuilles;
    }
    if (!tient(choisi, PAGE_HAUTEUR * nb)) {
      el.style.fontSize = `${TAILLE_MIN}px`;
      choisi = TAILLE_MIN;
      nb = Math.max(1, Math.ceil(el.scrollHeight / PAGE_HAUTEUR));
    }
    el.style.width = largeurAvant;
    el.style.fontSize = "";
    setBase(choisi);
    setAjuste(0);
    setPages(nb);
  }, [jours, salle, entete, unePage]);

  const saison = entete?.valeurs?.saison?.libelle || "";
  const semestre = entete?.semestre_courant ? `Semestre ${entete.semestre_courant}` : "";

  return (
    <div className="space-y-4 fiche-impression" data-testid="planning-affiche">
      <div className="flex flex-wrap items-center gap-3 sans-impression">
        <button onClick={onFermer} className="btn-ghost" data-testid="affiche-retour">
          <ArrowLeft size={15} /> Retour au planning
        </button>
        <div className="flex items-center gap-1 flex-wrap">
          <Onglet actif={salle === ""} onClick={() => { setSalle(""); setUnePage(false); }} testid="affiche-salle-toutes">
            Toutes les salles
          </Onglet>
          {sallesDispo.map((l) => (
            <Onglet key={l} actif={salle === l} onClick={() => { setSalle(l); setUnePage(true); }} testid={`affiche-salle-${l}`}>
              Salle {l}
            </Onglet>
          ))}
        </div>
        <button onClick={() => setUnePage(!unePage)} className="btn-ghost" data-testid="affiche-une-page"
          style={unePage ? { borderColor: "var(--primary)", color: "var(--primary)" } : undefined}
          title="Réduit le texte autant qu'il faut pour que tout tienne sur une seule feuille">
          {unePage ? "Texte lisible (plusieurs pages)" : "Tenir sur une page"}
        </button>
        <span className="text-xs text-[var(--muted)]" data-testid="affiche-format">
          {total} cours · {pages === 1 ? "une page" : `${pages} pages`}
          {unePage ? ` · texte ${Math.round(taille)} px` : (salle ? "" : " · une salle à la fois donne un texte plus grand")}
        </span>
        <div className="flex items-center gap-1">
          <button onClick={() => setAjuste(ajuste - 1)} className="btn-ghost"
            title="Texte plus petit" data-testid="affiche-texte-moins">A−</button>
          <button onClick={() => setAjuste(ajuste + 1)} className="btn-ghost"
            title="Texte plus grand (ajoute une page si besoin)" data-testid="affiche-texte-plus">A+</button>
        </div>
        <button onClick={() => window.print()} className="btn-primary ml-auto" data-testid="affiche-imprimer">
          <Printer size={15} /> Imprimer
        </button>
      </div>

      <p className="text-[11px] text-[var(--muted)] sans-impression" data-testid="affiche-conseil">
        Dans la fenêtre d'impression : <strong>Paysage</strong>, marges <strong>Par défaut</strong>, et
        dans « Plus de paramètres » décoche <strong>En-têtes et pieds de page</strong> — sinon la date
        et l'adresse du site s'impriment en haut et en bas de la feuille.
      </p>

      <div ref={feuille} className="affiche-planning feuille"
        style={{ fontSize: `${taille}px` }} data-testid="affiche-feuille">
        <div className="affiche-entete flex flex-wrap items-end justify-between gap-4 pb-3 mb-4 feuille-entete"
          style={{ borderBottom: "3px solid var(--primary)" }}>
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.28em] text-[var(--muted)]">
              1000 ARTS · Cugy VD · {[saison, semestre].filter(Boolean).join(" · ")}
            </div>
            <div className="font-display text-4xl tracking-tight leading-none mt-1">
              Planning de la semaine
            </div>
          </div>
          <div className="text-right">
            <div className="font-display text-3xl tracking-tight leading-none" style={{ color: "var(--primary)" }}
              data-testid="affiche-titre-salle">
              {salle ? `Salle ${salle}` : "Toutes les salles"}
            </div>
            <div className="text-[11px] text-[var(--muted)] mt-1">
              {total} cours · inscriptions sur 1000arts.ch
            </div>
          </div>
        </div>

        <div className="affiche-semaine">
          {jours.map((j) => (
            <div key={j.jour} className="affiche-colonne">
              <div className="pb-1 mb-2" style={{ borderBottom: "2px solid var(--primary)" }}>
                <div className="font-display uppercase leading-none"
                  style={{ fontSize: "1.5em", letterSpacing: "0.08em" }}>{j.jour}</div>
                <div className="text-[var(--muted)]" style={{ fontSize: "0.85em", marginTop: "0.25em" }}>
                  {j.cours.length} cours
                </div>
              </div>
              <div className="affiche-cartes" style={{ display: "flex", flexDirection: "column", gap: "0.45em" }}>
                {j.cours.length === 0 && (
                  <div className="text-[var(--muted)] italic" style={{ fontSize: "0.85em" }}>— aucun cours —</div>
                )}
                {j.cours.map((c) => {
                  const couleur = disciplineColor(c.discipline);
                  return (
                    <div key={c.id} className="affiche-carte bg-white"
                      style={{
                        border: "1px solid var(--border)",
                        borderLeft: `0.35em solid ${couleur}`,
                        borderRadius: "0.55em",
                        padding: "0.45em 0.55em",
                      }}>
                      <div className="flex items-center gap-1 flex-wrap">
                        <Clock size={Math.round(taille * 0.85)} style={{ color: couleur, flexShrink: 0 }} />
                        <span className="font-semibold tabular-nums" style={{ fontSize: "1.02em" }}>
                          {c.debut} <span className="text-[var(--muted)]">→</span> {c.fin}
                        </span>
                        <span className="text-[var(--muted)]" style={{ fontSize: "0.8em" }}>
                          {duree(c.duree_min)}
                        </span>
                        {c.age_affiche && (
                          <span className="ml-auto font-semibold whitespace-nowrap"
                            style={{
                              fontSize: "0.75em", background: `${couleur}22`, color: couleur,
                              borderRadius: "999px", padding: "0.1em 0.5em",
                            }}>
                            {c.age_affiche}
                          </span>
                        )}
                      </div>
                      <div className="font-display tracking-tight"
                        style={{ fontSize: "1.28em", lineHeight: 1.1, marginTop: "0.15em" }}>
                        {c.nom}
                      </div>
                      <div className="text-[var(--muted)] flex items-center gap-1"
                        style={{ fontSize: "0.85em", marginTop: "0.15em" }}>
                        <User size={Math.round(taille * 0.75)} style={{ flexShrink: 0 }} />
                        <span style={{ overflowWrap: "anywhere" }}>
                          {[c.professeur, c.niveau, !salle && c.salle].filter(Boolean).join(" · ")}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

const Onglet = ({ actif: on, onClick, testid, children }) => (
  <button onClick={onClick} data-testid={testid}
    className="text-xs font-semibold px-3 py-1.5 rounded-full transition-colors"
    style={on
      ? { background: "var(--primary)", color: "#fff" }
      : { background: "#fff", color: "var(--text)", border: "1px solid var(--border)" }}>
    {children}
  </button>
);
